import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';

interface HeaderProps {
  onNavigate: (page: string) => void;
  currentPage: string;
}

const Header: React.FC<HeaderProps> = ({ onNavigate, currentPage }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navItems = [
    { label: 'Tours', page: 'tours' },
    { label: 'About', page: 'about' },
    { label: 'Submission', page: 'submission' },
    { label: 'Gallery', page: 'gallery' },
    { label: 'Contact', page: 'contact' },
  ];

  const handleNavClick = (page: string) => {
    onNavigate(page);
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 w-full z-50 border-b border-slate-200/10 bg-background-light/90 dark:bg-background-dark/90 backdrop-blur-md transition-all duration-300">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-4 relative z-20 bg-background-light/0 dark:bg-background-dark/0">
        
        {/* Logo */}
        <button 
            onClick={() => handleNavClick('home')}
            className="flex-shrink-0 flex items-center space-x-2 sm:space-x-3 focus:outline-none group z-30"
        >
          <img 
            src="https://i.imgur.com/blse8Ux.png" 
            alt="Starroad Logo" 
            className="w-8 h-8 sm:w-10 sm:h-10 object-contain transition-transform group-hover:scale-110" 
          />
          <span className="font-bold tracking-tighter text-lg sm:text-xl font-display aurora-text">
            STARROAD
          </span>
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex flex-grow justify-center items-center space-x-6 lg:space-x-12 z-20">
          {navItems.map((item) => (
            <button 
              key={item.label}
              onClick={() => handleNavClick(item.page)}
              className={`
                text-[10px] lg:text-xs font-mono font-bold uppercase tracking-widest transition-colors relative group whitespace-nowrap
                ${currentPage === item.page ? 'text-primary' : 'text-slate-600 dark:text-white hover:text-primary'}
              `}
            >
              {item.label}
              <span className={`
                absolute -bottom-1 left-0 h-[1px] bg-primary transition-all duration-300
                ${currentPage === item.page ? 'w-full' : 'w-0 group-hover:w-full'}
              `}></span>
            </button>
          ))}
        </nav>

        {/* Hamburger / Right Balance */}
        <div className="flex-shrink-0 z-30 flex justify-end md:w-[140px]">
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 border border-slate-200 dark:border-slate-800 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="text-slate-900 dark:text-white" size={24} />
            ) : (
              <Menu className="text-slate-900 dark:text-white" size={24} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Dropdown */}
      <div className={`
        absolute top-full left-0 w-full bg-background-light dark:bg-black border-b border-slate-200 dark:border-slate-800 
        transform transition-all duration-300 ease-in-out md:hidden shadow-2xl
        ${isMenuOpen ? 'opacity-100 translate-y-0 visible' : 'opacity-0 -translate-y-4 invisible pointer-events-none'}
      `}>
        <nav className="flex flex-col p-6 items-center">
          {navItems.map((item) => (
            <button 
              key={item.label}
              onClick={() => handleNavClick(item.page)}
              className={`
                w-full text-center py-4 text-sm font-mono font-bold uppercase tracking-widest transition-colors border-b border-slate-200/50 dark:border-slate-800/50 last:border-0
                ${currentPage === item.page ? 'text-primary' : 'text-slate-900 dark:text-white hover:text-primary hover:bg-slate-50 dark:hover:bg-slate-900'}
              `}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </div>
    </header>
  );
};

export default Header;